// Minimal Capacitor Push registration that no-ops on web builds.
// On Android (Capacitor runtime), uses the global Plugins registry if available.

import { api } from './api';
import { logEvent } from './logging';

type PushRegistration = { value: string };

function getPushPlugin(): any | null {
  try {
    const w: any = window as any;
    // Capacitor v3+ exposes plugins under Capacitor.Plugins
    return w?.Capacitor?.Plugins?.PushNotifications || w?.PushNotifications || null;
  } catch {
    return null;
  }
}

function getPreferences(): any | null {
  try {
    const w: any = window as any;
    return w?.Capacitor?.Preferences || w?.Capacitor?.Plugins?.Preferences || null;
  } catch {
    return null;
  }
}

function getAppPlugin(): any | null {
  try {
    const w: any = window as any;
    return w?.Capacitor?.App || w?.Capacitor?.Plugins?.App || null;
  } catch {
    return null;
  }
}

async function setStoredPushToken(token: string): Promise<void> {
  try {
    const Preferences = getPreferences();
    if (Preferences) {
      await Preferences.set({ key: 'pushToken', value: token });
    } else {
      localStorage.setItem('pushToken', token);
    }
  } catch {}
}

async function getStoredPushToken(): Promise<string | null> {
  try {
    const Preferences = getPreferences();
    if (Preferences) {
      const res = await Preferences.get({ key: 'pushToken' });
      return res?.value || null;
    }
    return localStorage.getItem('pushToken');
  } catch {
    return null;
  }
}

async function syncPushToken(token: string, accessToken: string): Promise<void> {
  if (!token) return;
  const attempt = async (delayMs: number) => {
    try {
      if (delayMs > 0) await new Promise(res => setTimeout(res, delayMs));
      await api.postAuth('/push/register', { token, platform: 'android' }, accessToken);
      logEvent('push:register', { ok: true, tokenLen: token?.length || 0 });
      return true;
    } catch {
      return false;
    }
  };
  // Retry a couple times to tolerate transient network/server blips.
  const ok = await attempt(0) || await attempt(300) || await attempt(1000);
  if (!ok) {
    try { console.warn('Push token sync failed after retries'); } catch {}
    logEvent('push:register', { ok: false });
  }
}

let listenersBound = false;
let resumeListenerBound = false;

function bindPushListeners(Push: any, accessToken: string) {
  if (listenersBound) return;
  listenersBound = true;

  Push.addListener('registration', async (token: PushRegistration) => {
    try {
      await setStoredPushToken(token.value);
      await syncPushToken(token.value, accessToken);
      logEvent('push:registration', { ok: true });
    } catch {}
  });
  Push.addListener('registrationError', (err: any) => {
    try { console.warn('Push registration error', err); } catch {}
    logEvent('push:registration_error', { message: String(err?.message || err) });
    // Attempt a quick retry in case the plugin was in a bad state.
    try {
      setTimeout(() => {
        try { Push.register(); } catch {}
      }, 500);
    } catch {}
  });
}

function ensureRegisterOnResume(Push: any) {
  if (resumeListenerBound) return;
  resumeListenerBound = true;
  try {
    const App = getAppPlugin();
    if (App?.addListener) {
      App.addListener('resume', async () => {
        try { await Push.register(); } catch {}
      });
    }
  } catch {}
}

export async function initPush(accessToken: string) {
  try {
    const Push = getPushPlugin();
    if (!Push) return; // not running in Capacitor/Android

    // Re-sync any persisted token in case the process was killed while backgrounded.
    const existing = await getStoredPushToken();
    if (existing) {
      await syncPushToken(existing, accessToken);
    }

    bindPushListeners(Push, accessToken);
    ensureRegisterOnResume(Push);

    const perm = await Push.checkPermissions();
    if (perm?.receive !== 'granted') {
      const req = await Push.requestPermissions();
      if (req?.receive !== 'granted') return;
    }
    await Push.register();
    logEvent('push:init', { ok: true });
  } catch {}
}

export async function unregisterPush(accessToken: string) {
  try {
    const tok = await getStoredPushToken();
    if (!tok) return;
    await api.postAuth('/push/unregister', { token: tok }, accessToken);
  } catch {}
}
